import { FC, useState, MouseEvent, useMemo, ReactNode } from 'react';
import {
	Box,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	LinearProgress,
	ListSubheader,
	Menu,
	MenuItem,
	OutlinedInput,
	Popover,
	Stack,
	Typography,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import { z } from 'zod';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { getNightOrDayPalette } from '../../utils/getNightOrDayPalette';

type Item = Readonly<{
	value: string;
	label: string;
}>;

type Section = Readonly<{
	header: string;
	items: readonly Item[];
}>;

type Props = Readonly<
	(
		| {
				items: readonly Item[];
				sections?: null;
		  }
		| {
				items?: null;
				sections: readonly Section[];
		  }
	) & {
		value: string;
		placeholder?: string;
		disabled?: boolean;
		loading?: boolean;
		onChange?: (value: string) => void;
		onCreate?: (value: string) => void;
		createItemTitle: ReactNode;
		createDialogTitle: ReactNode;
		emptyItemErrorMessage: string;
	}
>;

const createFormSchema = (emptyItemErrorMessage: string) =>
	z.object({
		item: z.string().nonempty(emptyItemErrorMessage),
	});

type FormValues = z.TypeOf<ReturnType<typeof createFormSchema>>;

const CreatableSelect: FC<Props> = ({
	value,
	items,
	sections,
	onChange,
	onCreate,
	createItemTitle,
	createDialogTitle,
	disabled,
	loading,
	placeholder,
	emptyItemErrorMessage,
}) => {
	const intl = useIntl();
	const theme = useTheme();
	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [addItemAnchorEl, setAddTimeAnchorEl] = useState<null | HTMLElement>(null);
	const form = useTypedForm({
		schema: createFormSchema(emptyItemErrorMessage),
	});

	const onSelectClick = (event: MouseEvent<HTMLElement>) => {
		if (!loading && !disabled) {
			setMenuAnchorEl(event.currentTarget);
		}
	};

	const onMenuItemClick = (value: string) => () => {
		onChange?.(value);
		setMenuAnchorEl(null);
	};

	const onAddItemClick = (event: MouseEvent<HTMLElement>) => {
		setAddTimeAnchorEl(event.currentTarget);
	};

	const handleCreateItem = ({ item }: FormValues) => {
		setAddTimeAnchorEl(null);
		setMenuAnchorEl(null);
		onCreate?.(item);
	};

	const currentItem = useMemo(() => {
		const allItems = sections != null ? sections.flatMap(({ items }) => items) : items;
		return allItems.find((item) => item.value === value);
	}, [value, sections, items]);

	const currentLabel = loading ? intl.formatMessage({ id: 'common.loading' }) : (currentItem?.label ?? placeholder);

	const arrowColor = loading || disabled ? theme.palette.secondary.light : theme.palette.secondary.main;
	const textColor = loading || disabled ? theme.palette.secondary.light : theme.palette.text.primary;

	return (
		<>
			<Box position={'relative'}>
				<Button
					variant={'outlined'}
					fullWidth
					sx={{
						padding: currentLabel == null ? '9px 6px 9px 12px' : '7px 6px 7px 12px',
						fontSize: '1em',
						color: textColor,
						borderColor: theme.palette.secondary.light,
						display: 'flex',
						justifyContent: currentLabel == null ? 'flex-end' : 'space-between',
					}}
					onClick={onSelectClick}
				>
					{currentLabel}
					{menuAnchorEl == null ? <ArrowDropDownIcon sx={{ color: arrowColor }} /> : <ArrowDropUpIcon sx={{ color: arrowColor }} />}
				</Button>
				{loading === true && (
					<LinearProgress
						sx={{
							position: 'absolute',
							bottom: 0,
							width: '100%',
							height: 4,
							borderTopRightRadius: 0,
							borderTopLeftRadius: 0,
						}}
					/>
				)}
			</Box>
			<Menu
				anchorEl={menuAnchorEl}
				open={menuAnchorEl != null}
				onClose={() => setMenuAnchorEl(null)}
				sx={{ '.MuiMenu-list': { width: `${menuAnchorEl?.getBoundingClientRect().width}px`, maxHeight: 500, padding: 0 } }}
			>
				{(sections ?? [{ header: null, items }]).map(({ header, items }) => (
					<>
						{header != null && <ListSubheader>{header}</ListSubheader>}
						{items.map(({ value, label }) => (
							<MenuItem
								sx={{
									pl: header != null ? 4 : 2,
									gap: 1,
									margin: 0.5,
									borderRadius: 0.5,
								}}
								onClick={onMenuItemClick(value)}
							>
								{label}
							</MenuItem>
						))}
					</>
				))}
				<MenuItem
					sx={{
						backgroundColor: theme.palette.geekblue[0],
						color: theme.palette.primary[400],
						gap: 1,
						margin: 0.5,
						borderRadius: 0.5,
					}}
					onClick={onAddItemClick}
				>
					<Stack direction={'row'} gap={1}>
						<AddIcon sx={{ color: (theme) => theme.palette[getNightOrDayPalette(theme)][900] }} />
						<Typography color={(theme) => theme.palette[getNightOrDayPalette(theme)][900]}>{createItemTitle}</Typography>
					</Stack>
				</MenuItem>
			</Menu>
			<Popover
				anchorEl={addItemAnchorEl}
				open={addItemAnchorEl != null}
				onClose={() => setAddTimeAnchorEl(null)}
				anchorOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<form
					noValidate
					onSubmit={(event) => {
						form.handleSubmit(handleCreateItem)(event);
						event.stopPropagation();
					}}
				>
					<DialogTitle sx={{ padding: 2, pb: 0 }}>{createDialogTitle}</DialogTitle>
					<DialogContent sx={{ paddingX: 2, paddingBottom: 1 }}>
						<OutlinedInput {...form.register('item')} autoFocus sx={{ mt: 2 }} />
					</DialogContent>
					<DialogActions sx={{ paddingX: 2, pb: 2 }}>
						<Button variant={'contained'} type={'submit'}>
							<FormattedMessage id={'common.create'} />
						</Button>
					</DialogActions>
				</form>
			</Popover>
		</>
	);
};

export { CreatableSelect };
